/***** Response Types *****/
/** Represents the HTTP API response structure. */
export type UserResponse = User;

/***** Model Types *****/
type User = {
  id: string; // UUID
  dbid: number;
  isAlias?: boolean;

  name: string;
  email: string;
  phone?: string;

  subjects?: string[];
  grades?: string[];

  restrictions: Array<Restriction>;
  roles: Array<Role>;
};

type Restriction =
  | "RESTRICTION_CANNOT_EDIT_SCHOOL_PROFILE"
  | "RESTRICTION_CLASSROOM_VIEW_ONLY"
  | "RESTRICTION_CANNOT_CREATE_ADMINS"
  | "RESTRICTION_HIDE_SUB_FEEDBACK"
  | "RESTRICTION_CLASSROOM_REQUESTER"
  | "RESTRICTION_VIEW_ONLY_ADMIN";

type Role = "ROLE_ADMIN" | "ROLE_ADMIN_TX_VERIFIER" | "ROLE_BILLING_ADMIN" | "ROLE_SUPERADMIN";

/**
 * Model | User
 *
 * Represents a School Administrator user.
 *
 * @field restrictions - Restrictions that the user has.
 * @field roles - Roles that the user has.
 */
export class UserModel {
  private user?: User;

  constructor(user: UserResponse) {
    this.user = user;
  }

  get id() {
    return this.user?.id;
  }

  get dbid() {
    return this.user?.dbid;
  }

  get securityRestrictions(): string[] {
    return this.user?.restrictions || [""];
  }

  get email() {
    return this.user?.email || "";
  }

  get name() {
    return this.user?.name || "";
  }

  get roles() {
    return this.user?.roles || [];
  }

  get phone() {
    return this.user?.phone || "";
  }

  get isAlias() {
    return this.user?.isAlias;
  }

  get isSuperAdmin() {
    const storedValue = JSON.parse(localStorage.getItem("swing-view-as-admin") || "false");
    // Superadmin/admin views are getting confusing. If a user is aliased and
    // the localstorage item exists, return false for superadmin (show them
    // what a normal admin would see)

    // If aliased, return whatever's in localstorage
    if (this.isAlias) {
      return !storedValue;
    }
    // Otherwise, just return based on roles check
    if (this.user?.roles?.includes("ROLE_SUPERADMIN")) {
      return true;
    }
    return false;
  }

  get isTexasVerifier() {
    return this.user?.roles?.includes("ROLE_ADMIN_TX_VERIFIER");
  }

  get grades() {
    return this.user?.grades || [];
  }

  get subjects() {
    return this.user?.subjects || [];
  }

  get isBillingAdmin() {
    return this.user?.roles?.includes("ROLE_BILLING_ADMIN");
  }
}
